import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, History, KeyRound, PenSquare, Trash2 } from "lucide-react";
import clsx from "clsx";
import {
  Assistant,
  Skills,
  type AssistantAction,
  type Conversation,
  type Skill,
} from "@/api/assistant";
import { Work } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { confirmDialog, toast } from "@/lib/feedback";
import { fmtDayShort } from "@/lib/format";
import Composer from "@/components/assistant/Composer";
import { AssistantTurn, Spark, Turn, UserTurn } from "@/components/assistant/Turns";
import { NEW_CHAT, streamReply, useChatStore } from "@/store/chat";

function greeting(name: string | undefined): string {
  const h = new Date().getHours();
  const part = h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
  const first = (name ?? "").trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

// Skills offered as one-tap starting points on the empty screen.
const STARTERS = ["partner-brief", "research", "deal-review", "my-week"];

export default function HomePage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const convId = params.get("c");
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const restoredFor = useRef<string | null>(null);

  const lastChatId = useChatStore((s) => s.lastChatId);
  const setLastChatId = useChatStore((s) => s.setLastChatId);
  const live = useChatStore((s) => (convId ? s.live[convId] : undefined));
  const draftKey = convId ?? NEW_CHAT;
  const draft = useChatStore((s) => s.drafts[draftKey]);
  const setDraft = useChatStore((s) => s.setDraft);

  // Coming back to Home (the sidebar link is plain "/") reopens the last chat.
  useEffect(() => {
    if (!convId && lastChatId && !params.get("skill"))
      setParams({ c: lastChatId }, { replace: true });
  }, [convId, lastChatId, params, setParams]);
  useEffect(() => {
    if (convId) setLastChatId(convId);
  }, [convId, setLastChatId]);

  const statusQuery = useQuery({
    queryKey: ["assistant-status"],
    queryFn: Assistant.status,
    staleTime: 5 * 60_000,
  });
  const skillsQuery = useQuery({ queryKey: ["skills"], queryFn: Skills.list, staleTime: 60_000 });
  const convsQuery = useQuery({
    queryKey: ["assistant-conversations"],
    queryFn: Assistant.conversations,
  });
  const workQuery = useQuery({ queryKey: ["my-work"], queryFn: Work.mine, enabled: !convId });
  const detailQuery = useQuery({
    queryKey: ["assistant-conversation", convId],
    queryFn: () => Assistant.conversation(convId!),
    enabled: !!convId,
    retry: false,
    // A reply that's still being written elsewhere (another browser tab, a dropped stream).
    refetchInterval: (q) =>
      !live && q.state.data?.conversation.status === "running" ? 2000 : false,
  });

  // The remembered chat was deleted (or isn't ours): fall back to the landing page.
  useEffect(() => {
    if (convId && detailQuery.isError) {
      if (lastChatId === convId) setLastChatId(null);
      setParams({}, { replace: true });
    }
  }, [convId, detailQuery.isError, lastChatId, setLastChatId, setParams]);

  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  const draftSkill = draft?.skill ?? null;
  const skill = useMemo(
    () => (draftSkill ? (skills.find((s) => s.name === draftSkill) ?? null) : null),
    [draftSkill, skills],
  );
  const setText = (text: string) => setDraft(draftKey, { text, skill: draft?.skill ?? null });
  const setSkill = (s: Skill | null) =>
    setDraft(draftKey, { text: draft?.text ?? "", skill: s?.name ?? null });

  const actions = useMemo(() => {
    const map: Record<string, AssistantAction> = {};
    for (const a of detailQuery.data?.actions ?? []) map[a.id] = a;
    return map;
  }, [detailQuery.data]);

  // Deep links: /?skill=partner-brief starts a new chat with that skill picked (used by the Library).
  useEffect(() => {
    const wanted = params.get("skill");
    if (wanted && skills.length) {
      setDraft(NEW_CHAT, {
        text: "",
        skill: skills.some((s) => s.name === wanted) ? wanted : null,
      });
      setParams({}, { replace: true });
      setLastChatId(null);
    }
  }, [params, setParams, skills, setDraft, setLastChatId]);

  // Put the scroll position back where it was in this chat; new chats start at the bottom.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !convId || !detailQuery.data || restoredFor.current === convId) return;
    restoredFor.current = convId;
    const saved = useChatStore.getState().scroll[convId];
    if (saved == null) {
      el.scrollTop = el.scrollHeight;
      stickToBottom.current = true;
    } else {
      el.scrollTop = saved;
      stickToBottom.current = el.scrollHeight - saved - el.clientHeight < 120;
    }
  }, [convId, detailQuery.data]);

  // Follow the reply as it streams, unless the user scrolled up to read.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current && restoredFor.current === convId)
      el.scrollTop = el.scrollHeight;
  }, [live, detailQuery.data, convId]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || !convId) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    useChatStore.getState().setScroll(convId, el.scrollTop);
  };

  const send = async (text: string) => {
    const chosen = skill?.name ?? null;
    let id = convId;
    try {
      if (!id) {
        id = (await Assistant.createConversation()).id;
        setDraft(NEW_CHAT, { text: "", skill: null });
        restoredFor.current = id; // brand-new chat: nothing to restore
        setParams({ c: id });
      } else {
        setDraft(id, { text: "", skill: null });
      }
    } catch (e) {
      toast.err(e instanceof Error ? e.message : String(e));
      return;
    }
    stickToBottom.current = true;
    // Runs outside this component: leaving the page doesn't stop the reply.
    void streamReply(qc, id, text, chosen);
  };

  const newChat = () => {
    setLastChatId(null);
    setParams({});
  };

  const removeConversation = async (c: Conversation) => {
    const ok = await confirmDialog({
      title: "Delete this chat?",
      body: `“${c.title}” will be removed. Documents it created stay in the Library.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    await Assistant.deleteConversation(c.id);
    if (c.id === lastChatId) setLastChatId(null);
    if (c.id === convId) newChat();
    void qc.invalidateQueries({ queryKey: ["assistant-conversations"] });
    toast.ok("Chat deleted");
  };

  const configured = statusQuery.data?.configured ?? true;
  const busy = !!live || detailQuery.data?.conversation.status === "running";
  const inChat = !!convId;
  const messages = detailQuery.data?.messages ?? [];
  const recent = (convsQuery.data ?? []).slice(0, 4);
  const workCount = workQuery.data
    ? workQuery.data.handoffs.length +
      workQuery.data.tasks.filter((t) => t.due_date && new Date(t.due_date) <= new Date()).length +
      workQuery.data.due_deals.length +
      workQuery.data.quiet_deals.length
    : 0;

  const composer = (
    <Composer
      text={draft?.text ?? ""}
      onText={setText}
      skills={skills}
      skill={skill}
      onSkill={setSkill}
      onSend={(t) => void send(t)}
      busy={busy}
      disabled={!configured}
      autoFocus
    />
  );

  // About to reopen the last chat: render nothing rather than flash the landing page.
  if (!convId && lastChatId && !params.get("skill")) return null;

  return (
    <div className="relative h-[calc(100dvh-3rem)] md:h-screen flex flex-col">
      {/* Top-right: history + new chat */}
      <div className="absolute top-2 right-3 sm:top-4 sm:right-5 z-30 flex items-center gap-1 rounded-full bg-ink-950/80 backdrop-blur sm:bg-transparent sm:backdrop-blur-none">
        {inChat && (
          <button className="btn !py-1.5 shadow-sm" onClick={newChat} title="New chat (N)">
            <PenSquare className="w-4 h-4 text-accent-violet" /> New chat
          </button>
        )}
        <div className="relative">
          <button
            className="btn btn-ghost !px-2"
            aria-label="Chat history"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((o) => !o)}
          >
            <History className="w-4 h-4" />
          </button>
          {historyOpen && (
            <>
              <div className="fixed inset-0" onClick={() => setHistoryOpen(false)} />
              <div className="panel absolute right-0 mt-1.5 w-[min(340px,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto p-1.5 animate-fade-up">
                <div className="section-title px-3 pt-2 pb-1.5">Your chats</div>
                {(convsQuery.data ?? []).length === 0 && (
                  <div className="px-3 py-4 text-sm text-mut-500">No chats yet.</div>
                )}
                {(convsQuery.data ?? []).map((c) => (
                  <div
                    key={c.id}
                    className={clsx(
                      "group flex items-center gap-2 rounded-xl px-3 py-2 hover:bg-fg/[0.04]",
                      c.id === convId && "bg-fg/[0.05]",
                    )}
                  >
                    <button
                      className="flex-1 min-w-0 text-left"
                      onClick={() => {
                        setHistoryOpen(false);
                        setParams({ c: c.id });
                      }}
                    >
                      <div className="text-md truncate">{c.title}</div>
                      <div className="text-xs text-mut-500">{fmtDayShort(c.updated_at)}</div>
                    </button>
                    <button
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-mut-500 hover:text-accent-pink p-1"
                      aria-label={`Delete ${c.title}`}
                      onClick={() => void removeConversation(c)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {!inChat ? (
        // ── Landing: greeting + composer, nothing else competing for attention ──
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-10 sm:px-5 sm:py-16">
            <div className="w-full max-w-[720px]">
              <h1 className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3.5 font-display text-[34px] sm:text-[44px] leading-tight tracking-[-0.02em] text-mut-100 mb-7 sm:mb-9 text-balance text-center">
                <Spark size={40} />
                {greeting(user?.name)}
              </h1>

              {composer}

              {!configured && <NotConfigured />}

              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {STARTERS.map((name) => skills.find((s) => s.name === name))
                  .filter((s): s is Skill => !!s)
                  .map((s) => (
                    <button
                      key={s.id}
                      onClick={() => {
                        setSkill(s);
                        document.getElementById("assistant-input")?.focus();
                      }}
                      className="px-3.5 py-1.5 rounded-full border border-fg/[0.1] bg-ink-900 text-sm text-mut-300 hover:text-mut-100 hover:border-fg/[0.18] transition-colors"
                    >
                      {s.title}
                    </button>
                  ))}
              </div>

              <div className="mt-10 sm:mt-12 grid gap-6 sm:grid-cols-2 [&>*]:min-w-0">
                {recent.length > 0 && (
                  <div>
                    <div className="section-title mb-2">Recent chats</div>
                    <div className="space-y-0.5">
                      {recent.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setParams({ c: c.id })}
                          className="w-full text-left row-hover px-3 py-2 text-md truncate text-mut-200"
                        >
                          {c.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {workCount > 0 && (
                  <div>
                    <div className="section-title mb-2">Today</div>
                    <Link
                      to="/today"
                      className="flex items-center gap-3 rounded-2xl border border-fg/[0.08] bg-ink-900 px-4 py-3 hover:border-fg/[0.16] transition-colors"
                    >
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-accent-violet/10 text-accent-violet font-semibold text-sm">
                        {workCount}
                      </span>
                      <span className="flex-1 text-md">
                        {workCount === 1 ? "thing needs" : "things need"} you today
                      </span>
                      <ArrowRight className="w-4 h-4 text-mut-500" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // ── A conversation ──
        <>
          <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[760px] px-4 sm:px-5 pt-14 sm:pt-16 pb-8 space-y-7">
              {detailQuery.isLoading && (
                <div className="text-mut-500 text-md">Loading the chat…</div>
              )}
              {detailQuery.isError && (
                <div className="text-accent-pink text-md">This chat couldn't be loaded.</div>
              )}
              {messages.map((m) => (
                <Turn key={m.id} message={m} actions={actions} />
              ))}
              {live && (
                <>
                  <UserTurn part={{ type: "text", text: live.user.text, skill: live.user.skill }} />
                  <AssistantTurn parts={live.parts} actions={actions} thinking />
                </>
              )}
              {!live && detailQuery.data?.conversation.status === "running" && (
                <AssistantTurn parts={[]} actions={actions} thinking />
              )}
            </div>
          </div>
          <div className="px-3 sm:px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-5 pt-2 bg-gradient-to-t from-ink-950 via-ink-950 to-transparent">
            <div className="mx-auto w-full max-w-[760px]">
              {composer}
              {!configured && <NotConfigured />}
              <p className="hidden sm:block mt-2 text-center text-xs text-mut-500">
                The assistant can make mistakes. Changes to the CRM always wait for your approval.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NotConfigured() {
  return (
    <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-accent-amber/30 bg-accent-amber/[0.07] px-4 py-3 text-sm">
      <KeyRound className="w-4 h-4 mt-0.5 text-accent-amber shrink-0" />
      <span>
        The assistant isn't set up yet. An admin needs to add{" "}
        <span className="font-mono">ANTHROPIC_API_KEY</span> to the backend's secret (or{" "}
        <span className="font-mono">.env</span> locally).
      </span>
    </div>
  );
}
